/**
 * Custom Form
 * Posts to /apps/ai/forms/:slug via Shopify App Proxy.
 * Files go to R2 via presigned PUT, then backend mirrors to Dropbox + emails recipients.
 */
class CustomForm extends HTMLElement {
  connectedCallback() {
    this.slug = this.dataset.slug;
    this.proxy = (this.dataset.proxy || '/apps/ai').replace(/\/$/, '');
    this.successMsg = this.dataset.success || 'Thanks — submitted.';
    this.successRedirect = this.dataset.successRedirect || '';
    this.eventName = this.dataset.eventName || 'pickawall:form-success';
    this.turnstileSite = this.dataset.turnstileSite || '';
    this.form = this.querySelector('form');
    this.statusEl = this.querySelector('.custom-form__banner');
    this.submitBtn = this.querySelector('button[type="submit"]');
    if (!this.form) return;
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
  }

  getTurnstileToken() {
    if (!this.turnstileSite) return '';
    const el = this.querySelector('input[name="cfTurnstileToken"]');
    return (el && el.value) || '';
  }

  setStatus(msg, kind) {
    if (this.statusEl) {
      this.statusEl.textContent = msg;
      this.statusEl.dataset.kind = kind || '';
      this.statusEl.hidden = !msg;
    }
    this.dataset.state = kind || '';
  }

  async handleSubmit(e) {
    e.preventDefault();
    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }
    this.submitBtn.disabled = true;
    this.setStatus('Sending…', 'pending');

    try {
      // 1. Upload each file via a presigned R2 URL.
      const uploadedFiles = [];
      const fileInputs = this.querySelectorAll('input[data-cf-file="1"]');
      for (const input of fileInputs) {
        const files = Array.from(input.files || []);
        for (const file of files) {
          this.setStatus(`Uploading ${file.name}…`, 'pending');
          const presignRes = await fetch(`${this.proxy}/forms/${encodeURIComponent(this.slug)}/file-url`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              filename: file.name,
              contentType: file.type || 'application/octet-stream',
              size: file.size,
            }),
          });
          if (!presignRes.ok) {
            const data = await presignRes.json().catch(() => ({}));
            if (data.error === 'file_too_large') {
              throw new Error(`${file.name} is too large (max ${data.maxMb}MB).`);
            }
            throw new Error(`Could not start upload for ${file.name}.`);
          }
          const { key, uploadUrl } = await presignRes.json();
          const putRes = await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
            body: file,
          });
          if (!putRes.ok) throw new Error(`Upload failed for ${file.name}.`);
          uploadedFiles.push({
            name: file.name,
            r2Key: key,
            contentType: file.type || 'application/octet-stream',
            size: file.size,
          });
        }
      }

      // 2. Collect non-file fields.
      const fields = {};
      const fd = new FormData(this.form);
      for (const [k, v] of fd.entries()) {
        if (k === 'hp') continue;
        if (k === 'cfTurnstileToken') continue;
        if (v instanceof File) continue;
        fields[k] = typeof v === 'string' ? v : String(v);
      }

      // 2b. Bot protection — require Turnstile token if widget is enabled.
      const cfTurnstileToken = this.getTurnstileToken();
      if (this.turnstileSite && !cfTurnstileToken) {
        throw new Error('Please complete the bot check before submitting.');
      }

      // 3. Submit metadata.
      this.setStatus('Sending…', 'pending');
      const res = await fetch(`${this.proxy}/forms/${encodeURIComponent(this.slug)}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          fields,
          files: uploadedFiles,
          sourcePage: location.pathname,
          hp: fd.get('hp') || '',
          cfTurnstileToken,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.error === 'captcha_failed') {
          throw new Error('Bot check failed. Please refresh and try again.');
        }
        throw new Error('Submission failed. Please try again.');
      }
      const result = await res.json().catch(() => ({}));
      this.form.reset();
      if (window.turnstile && this.turnstileSite) {
        try { window.turnstile.reset(); } catch (_) { /* noop */ }
      }
      this.setStatus(this.successMsg, 'success');

      // Notify analytics / pixels (Meta, GA4, Google Ads, TikTok, ...).
      try {
        window.dispatchEvent(new CustomEvent(this.eventName, {
          detail: {
            slug: this.slug,
            submissionId: result && result.id,
            sourcePage: location.pathname,
            fields,
          },
        }));
      } catch (_) { /* noop */ }

      if (this.successRedirect) {
        window.location.assign(this.successRedirect);
      }
    } catch (err) {
      console.error('[custom-form]', err);
      this.setStatus(err.message || 'Something went wrong. Please try again.', 'error');
    } finally {
      // Re-enable submit only if we did not finish in success state.
      if (this.dataset.state !== 'success') {
        this.submitBtn.disabled = false;
      }
    }
  }
}

if (!customElements.get('custom-form')) {
  customElements.define('custom-form', CustomForm);
}
